import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { View, Text, Pressable, ScrollView, Platform, Modal } from 'react-native'
import { ChevronDown, Check } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { getSeatBucketPickerStyles } from './SeatBucketPicker.styles'
import type { BusinessSeatBucket } from '../../constants/pricingPlans.constants'

interface SeatBucketPickerProps {
  buckets: readonly BusinessSeatBucket[]
  selectedKey: string
  onSelect: (key: string) => void
  disabled?: boolean
  /**
   * Pairs with a sibling control (e.g. Upgrade) in one row: no label, no vertical
   * margins, trigger stretches to match height/width of the row.
   */
  inlineRow?: boolean
}

/**
 * In-card employee tier picker.
 * Uses a Modal (same pattern as ActionMenuDropdown) so the panel renders at the
 * root level and is never clipped or obscured by any parent stacking context.
 */
export function SeatBucketPicker({
  buckets,
  selectedKey,
  onSelect,
  disabled = false,
  inlineRow = false,
}: SeatBucketPickerProps) {
  const theme = useTheme()
  const styles = useMemo(() => getSeatBucketPickerStyles(theme), [theme])
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const triggerRef = useRef<View>(null)

  const selected = useMemo(
    () => buckets.find((b) => b.key === selectedKey) ?? buckets[0],
    [buckets, selectedKey],
  )

  const openMenu = useCallback(() => {
    triggerRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setMenuPos({ top: pageY + height + 8, left: pageX, width })
      setOpen(true)
    })
  }, [])

  const closeMenu = useCallback(() => setOpen(false), [])

  // Close the menu if the page scrolls — the modal position is fixed at open-time
  // so it would drift away from the trigger on scroll.
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return
    const handler = () => closeMenu()
    window.addEventListener('scroll', handler, { passive: true, capture: true })
    return () => window.removeEventListener('scroll', handler, { capture: true })
  }, [open, closeMenu])

  const toggle = useCallback(() => {
    if (disabled) return
    if (open) closeMenu()
    else openMenu()
  }, [disabled, open, openMenu, closeMenu])

  useEffect(() => {
    if (disabled) closeMenu()
  }, [disabled, closeMenu])

  const pick = useCallback(
    (key: string) => {
      onSelect(key)
      closeMenu()
    },
    [onSelect, closeMenu],
  )

  return (
    <View
      style={[
        styles.container,
        inlineRow && styles.containerInlineRow,
      ]}
    >
      {inlineRow ? null : <Text style={styles.label}>Employees</Text>}

      <View ref={triggerRef} style={inlineRow ? styles.triggerInlineRowWrap : undefined}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open, disabled }}
          accessibilityLabel={`Employee tier, ${selected.tagline}, ${selected.priceLabel}`}
          disabled={disabled}
          onPress={toggle}
          style={({ pressed, hovered }) => [
            styles.trigger,
            inlineRow && styles.triggerInlineRow,
            disabled && styles.triggerDisabled,
            pressed && !disabled && styles.triggerPressed,
            Platform.OS === 'web' && hovered && !disabled && styles.triggerHovered,
          ]}
        >
          <Text style={styles.triggerText} numberOfLines={2}>
            {`${selected.tagline} — ${selected.priceLabel}`}
          </Text>
          <ChevronDown
            size={22}
            color={theme.gray[600]}
            style={[
              styles.triggerChevron,
              Platform.OS === 'web' && open ? styles.triggerChevronOpen : null,
            ]}
          />
        </Pressable>
      </View>

      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={closeMenu}
        statusBarTranslucent
      >
        <Pressable style={styles.modalBackdrop} onPress={closeMenu}>
          {menuPos != null ? (
            <View
              // Inline style is measure-derived (trigger position + width) — cannot live
              // in StyleSheet because the values change per open. The static visuals come
              // from styles.menu.
              style={[styles.menu, { top: menuPos.top, left: menuPos.left, width: menuPos.width }]}
              onStartShouldSetResponder={() => true}
            >
              <ScrollView
                style={[styles.menuScroll, Platform.OS === 'web' && styles.menuScrollWeb]}
                contentContainerStyle={styles.menuScrollContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                showsVerticalScrollIndicator
                bounces={false}
              >
                {buckets.map((bucket, index) => {
                  const isSelected = bucket.key === selectedKey
                  const isLast = index === buckets.length - 1
                  return (
                    <Pressable
                      key={bucket.key}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isSelected }}
                      accessibilityLabel={bucket.tagline}
                      onPress={() => pick(bucket.key)}
                      style={({ pressed, hovered }) => [
                        styles.menuItem,
                        isLast && styles.menuItemLast,
                        isSelected && styles.menuItemSelected,
                        pressed && styles.menuItemPressed,
                        Platform.OS === 'web' && hovered && styles.menuItemHovered,
                      ]}
                    >
                      <Text
                        style={[styles.menuItemText, isSelected && styles.menuItemTextSelected]}
                        numberOfLines={2}
                      >
                        {`${bucket.tagline} — ${bucket.priceLabel}`}
                      </Text>
                      {isSelected ? (
                        <Check size={18} color={theme.primary} strokeWidth={2.5} />
                      ) : (
                        <View style={styles.menuItemCheckSpacer} />
                      )}
                    </Pressable>
                  )
                })}
              </ScrollView>
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </View>
  )
}
